import { Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { IGlobalState } from '~reducer';
import Layout from './Layout';
import { currencyFormatter } from '~utilities/formatters';
import { Dispatch } from 'redux';
import { IDictionary } from '~utilities/IDictionary';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import { abaTransactionsSelector } from '~WarehouseLoanTransactionAbas/selectors';
import { abaTransactionsListAction } from '~WarehouseLoanTransactionAbas/actions';

export default function Transactions(): ReactElement {
    const { warehouseLoanTransactionAbaUuid } = useParams<{ warehouseLoanTransactionAbaUuid: string }>();

    const transactions: IDictionary<IWarehouseLoanTransaction> = useSelector((state: IGlobalState) => abaTransactionsSelector(state, warehouseLoanTransactionAbaUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!transactions) {
            dispatch(abaTransactionsListAction(warehouseLoanTransactionAbaUuid));
        }
    }, [
        dispatch,
        warehouseLoanTransactionAbaUuid,
        transactions,
    ]);

    const summary: (data: readonly IWarehouseLoanTransaction[]) => JSX.Element = useCallback((data: readonly IWarehouseLoanTransaction[]): JSX.Element => {
        const totalInterest: number = _.reduce(data, (totalInterest: number, transaction: IWarehouseLoanTransaction) => totalInterest + transaction.amountPrepaidInterest, 0);

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                <Table.Summary.Cell index={1}>{data.length}</Table.Summary.Cell>
                <Table.Summary.Cell index={2}>{currencyFormatter.format(totalInterest)}</Table.Summary.Cell>
            </Table.Summary.Row>
        );
    }, []);

    if (!transactions) {
        return (
            <Layout
                pageTitle='Transactions'
                section='transactions'
                uuid={warehouseLoanTransactionAbaUuid}
            >
                <Typography.Title level={2}>Transactions</Typography.Title>
                <Spin/>
            </Layout>
        );
    }

    const columns: ColumnsType<IWarehouseLoanTransaction> = [
        {
            dataIndex: 'transactionTime',
            defaultSortOrder: 'descend',
            render: (warehouseLoanTransactionTime: string) => dayjs(warehouseLoanTransactionTime).format('DD/MM/YYYY HH:mm:ss'),
            sorter: (a: IWarehouseLoanTransaction, b: IWarehouseLoanTransaction) => dayjs(a.transactionTime) > dayjs(b.transactionTime) ? 1 : -1,
            title: 'Transaction Time',
            width: 220,
        },
        {
            dataIndex: 'loanCode',
            render: (loanCode: string, row: IWarehouseLoanTransaction): ReactNode => <Link to={`/loans/${row.loanUuid}`}>{loanCode}</Link>,
            title: 'Loan',
        },
        {
            dataIndex: 'amountPrepaidInterest',
            render: (amountPrepaidInterest: number) => currencyFormatter.format(amountPrepaidInterest),
            title: 'Prepaid Interest',
            width: 200,
        },
    ];

    return (
        <Layout
            pageTitle='Transactions'
            section='transactions'
            uuid={warehouseLoanTransactionAbaUuid}
        >
            <Typography.Title level={2}>Transactions</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(transactions)}
                pagination={false}
                rowKey='loanTransactionUuid'
                size='middle'
                summary={summary}
            />
        </Layout>
    );
}